import React, { useEffect, useState } from "react";
import { Check, Crown, Download, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fmtErr } from "../../lib/api";
import { useCredits } from "../../lib/useCredits";
import { useAuth } from "../../context/AuthContext";
import { BillingToggle, PlanPrice } from "../../components/BillingToggle";

export default function Compte() {
  const { user, setUser, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState("");
  const planFeatures = t("compte.planFeatures");
  const credits = useCredits();
  const [interval, setBillingInterval] = useState("month");

  useEffect(() => { api.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => {}); }, []);
  // La périodicité affichée suit l'abonnement réel (mensuel/annuel lu côté serveur)
  useEffect(() => { if (sub?.has_subscription && sub?.interval) setBillingInterval(sub.interval); }, [sub?.has_subscription, sub?.interval]);
  // Clic sur la jauge sidebar (#abonnement) : scroll fluide jusqu'à la section Abonnement
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#abonnement") {
      document.querySelector('[data-testid="subscription-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  // Reprise de l'intention d'offre venue de la landing après authentification.
  // Le backend reste l'autorité : workspace réel, plan actuel et action Stripe
  // autorisée sont revérifiés côté serveur dans /payments/checkout et /billing/change-plan.
  const wanted = searchParams.get("subscribe");
  useEffect(() => {
    if (wanted && sub) {
      sessionStorage.removeItem("elvarify-intent-plan");
      setSearchParams({}, { replace: true });
      if (wanted !== sub.plan) checkout(wanted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, wanted]);

  const saveName = async () => {
    await api.patch("/account", { name });
    setUser({ ...user, name });
    toast.success(t("compte.nameUpdated"));
  };

  const checkout = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      if (sub?.has_subscription) {
        // Abonnement existant → changement d'offre direct (prorata), sans nouveau checkout
        const { data } = await api.post("/billing/change-plan", { lookup_key: lookupKey });
        if (data.scheduled) {
          // Downgrade : effet au prochain renouvellement
          toast.success(t("compte.planScheduled", { plan: data.plan?.toUpperCase() }));
          const r = await api.get("/billing/subscription");
          setSub(r.data);
          setBusy("");
          return;
        }
        if (data.changed) {
          toast.success(t("compte.planChanged"));
          const r = await api.get("/billing/subscription");
          setSub(r.data);
          setBusy("");
          return;
        }
      }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e, t("compte.checkoutFail")));
      setBusy("");
    }
  };

  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/billing/invoices").then((r) => setInvoices(r.data.invoices)).catch(() => {}); }, []);

  const manageSubscription = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(fmtErr(e, t("compte.noSub")));
      setBusy("");
    }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "elvarify-export.json";
      a.click();
      toast.success(t("compte.exported"));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteAccount = async () => {
    if (!window.confirm(t("compte.deleteConfirm"))) return;
    await api.delete("/account");
    await logout();
    navigate("/");
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="compte-page">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{t("compte.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("compte.subtitle")}</p>
      </div>

      <section className="rounded-2xl border border-border bg-card p-7" data-testid="profile-section">
        <h2 className="font-display font-bold text-lg mb-5">{t("compte.profile")}</h2>
        <div className="flex items-center gap-4 mb-5">
          <div className="h-14 w-14 rounded-full bg-electric/15 text-electric flex items-center justify-center text-lg font-black">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-bold">{user?.email}</p>
            <p className="text-[11px] text-muted-foreground capitalize">{t("compte.role", { role: user?.role })} · {user?.provider === "google" ? t("compte.googleAccount") : t("compte.emailAccount")}</p>
          </div>
        </div>
        <div className="flex gap-2 max-w-md">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name"
            className="flex-1 rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-electric" />
          <button onClick={saveName} data-testid="profile-save"
            className="rounded-full bg-electric text-white px-5 py-2.5 text-xs font-bold hover:bg-blue-700 transition-colors flex items-center gap-1.5">
            <Save size={12} /> {t("common.save")}
          </button>
        </div>
      </section>

      <section data-testid="subscription-section" className="scroll-mt-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-lg">{t("compte.subscription")}</h2>
          {sub && (
            <span className="text-xs text-muted-foreground flex items-center gap-2">
              {t("compte.yourOffer")}
              <span className="rounded-full bg-electric/10 text-electric px-3 py-1 text-[11px] font-black tracking-wide" data-testid="current-plan-badge">
                {sub.plan_name}
              </span>
              {sub.plan !== "free" && <span className="text-success font-semibold">{t("compte.active")}</span>}
            </span>
          )}
        </div>
        {sub?.pending_plan && (
          <p className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2.5 mb-4" data-testid="pending-plan-notice">
            {t("compte.pendingPlan", { plan: sub.pending_plan.toUpperCase() })}
          </p>
        )}
        {credits?.mode === "quota" && (
          <div className="rounded-xl border border-border bg-card px-4 py-3 mb-5" data-testid="usage-section">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold">{t("usage.monthly", { pct: credits.cycle_percent })}</span>
              {credits.cycle_end && (
                <span className="text-muted-foreground">
                  {t("compte.renewalOn", { date: new Date(credits.cycle_end).toLocaleDateString() })}
                </span>
              )}
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-border overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${credits.exhausted ? "bg-destructive" : credits.warning ? "bg-amber-500" : "bg-electric"}`}
                style={{ width: `${Math.min(100, credits.cycle_percent)}%` }} />
            </div>
          </div>
        )}
        <div className="mb-5">
          <BillingToggle interval={interval} onChange={setBillingInterval} testid="compte-billing-toggle" />
        </div>
        <div className="grid md:grid-cols-3 gap-4 items-stretch">
          {(sub?.plans || []).map((p) => {
            const subInterval = sub?.interval || "month";
            const currentPlan = sub?.plan === p.key;
            const isCurrent = currentPlan && (!sub?.has_subscription || subInterval === interval);
            const ownsOtherInterval = currentPlan && sub?.has_subscription && subInterval !== interval;
            const lookupKey = interval === "year" ? p.lookup_key_yearly : p.lookup_key;
            return (
              <div key={p.key} className={`rounded-2xl border p-6 flex flex-col ${currentPlan ? "border-electric bg-electric/5" : "border-border bg-card"}`} data-testid={`compte-plan-${p.key}`}>
                {currentPlan && <span className="text-[9px] font-black text-electric uppercase tracking-wider flex items-center gap-1 mb-2" data-testid={`compte-plan-${p.key}-current`}><Crown size={11} /> {t("compte.current")}{sub?.has_subscription && ` · ${t(subInterval === "year" ? "pricing.intervalYearly" : "pricing.intervalMonthly")}`}</span>}
                <h3 className="font-display font-bold">{p.name}</h3>
                <PlanPrice priceMonth={p.price_eur} priceYear={p.price_year_eur} interval={interval} />
                <ul className="mt-4 mb-6 space-y-1.5 flex-1">
                  {(planFeatures[p.key] || []).map((f) => (
                    <li key={f} className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Check size={11} className="text-success" /> {f}</li>
                  ))}
                </ul>
                <button onClick={() => checkout(lookupKey)} disabled={isCurrent || !!busy} data-testid={`compte-subscribe-${p.key}`}
                  className={`w-full rounded-full py-2.5 text-xs font-bold transition-colors mt-auto ${isCurrent ? "bg-secondary text-muted-foreground cursor-default" : "bg-electric text-white hover:bg-blue-700"} disabled:opacity-60`}>
                  {isCurrent ? t("compte.current")
                    : busy === lookupKey ? t("compte.redirecting")
                    : ownsOtherInterval ? (interval === "year" ? t("compte.switchToYearly") : t("compte.switchToMonthly"))
                    : sub?.plan === "free" ? t("compte.choose", { name: p.name })
                    : t("compte.upgradeTo", { name: p.name })}
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-5 text-[10px] text-muted-foreground leading-relaxed" data-testid="compte-legal-consent">
          {t("legal.consentPre")}
          <Link to="/conditions" className="text-electric hover:underline">{t("legal.docNames.conditions")}</Link>
          {t("legal.consentMid")}
          <Link to="/confidentialite" className="text-electric hover:underline">{t("legal.docNames.confidentialite")}</Link>
          {t("legal.consentEnd")}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button onClick={manageSubscription} disabled={!!busy} data-testid="manage-subscription"
            className="rounded-full border border-border px-5 py-2.5 text-xs font-bold hover:border-electric/50 hover:text-electric transition-colors disabled:opacity-60">
            {busy === "portal" ? t("compte.opening") : t("compte.manage")}
          </button>
          <span className="text-[10px] text-muted-foreground">{t("compte.stripeNote")}</span>
        </div>
        {invoices.length > 0 && (
          <div className="mt-5 rounded-xl border border-border divide-y divide-border/60" data-testid="invoices-list">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 px-4 py-3 text-xs">
                <span className="font-semibold">{inv.number || inv.id}</span>
                <span className="text-muted-foreground">{new Date(inv.created * 1000).toLocaleDateString("fr-FR")}</span>
                <span className="font-bold">{inv.amount.toFixed(2)} {inv.currency}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${inv.status === "paid" ? "bg-success/10 text-success" : "bg-secondary text-muted-foreground"}`}>{inv.status}</span>
                {inv.pdf && <a href={inv.pdf} target="_blank" rel="noreferrer" className="ml-auto text-electric hover:underline font-semibold">PDF</a>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-7" data-testid="data-section">
        <h2 className="font-display font-bold text-lg mb-4">{t("compte.data")}</h2>
        <div className="flex flex-wrap gap-3">
          <button onClick={exportData} data-testid="export-data-btn"
            className="rounded-full border border-border px-5 py-2.5 text-xs font-bold hover:border-electric/50 hover:text-electric transition-colors flex items-center gap-1.5">
            <Download size={13} /> {t("compte.export")}
          </button>
          <button onClick={deleteAccount} data-testid="delete-account-btn"
            className="rounded-full border border-destructive/40 text-destructive px-5 py-2.5 text-xs font-bold hover:bg-destructive/10 transition-colors flex items-center gap-1.5">
            <Trash2 size={13} /> {t("compte.deleteAccount")}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">{t("compte.deleteNote")}</p>
      </section>
    </div>
  );
}
